import { useEffect, useMemo, useRef } from 'react';

import Tracker from '~/src/common/services/Tracker';
import { round } from '~/src/common/utils/number';
import { useRecipeScreenAnalyticsContext } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/context';
import {
  Recipe,
  RecipeIngredientProductArticle,
} from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/types';

interface Props {
  recipe?: Recipe;
  ingredientsArticles: RecipeIngredientProductArticle[];
}

const useRecipeAnalytics = ({ recipe, ingredientsArticles }: Props) => {
  const lastRecipeIdEventSent = useRef<string>();
  const { eventSource, categoryName, subcategoryName } = useRecipeScreenAnalyticsContext();

  const analyticsProperties = useMemo(
    () => ({
      'current page path': `/recette/${recipe?.slugs[0] ?? ''}`,
      'current page type viewed': 'recipe page',
    }),
    [recipe?.slugs],
  );
  useEffect(() => {
    if (recipe && ingredientsArticles.length > 0 && lastRecipeIdEventSent.current !== recipe.id) {
      lastRecipeIdEventSent.current = recipe.id;

      const outOfStockProducts = ingredientsArticles.filter(a => a.availableQuantity === 0);
      const outOfStockProductsSimilar = outOfStockProducts.filter(a => a.hasSubstituteProducts);

      Tracker.sendEvent('recipe page viewed', {
        'is page viewed': true,
        'recipe id': recipe.id,
        'recipe name': recipe.name,
        'recipe type': recipe.type,
        'recipe event source': eventSource,
        'recipe category': categoryName,
        'recipe subcategory': subcategoryName,
        'current page name': recipe.name,
        'in stock products': ingredientsArticles.length - outOfStockProducts.length,
        'out of stock products': outOfStockProducts.length,
        'similar products activated': outOfStockProductsSimilar.length,
        'similar products activated rate':
          outOfStockProducts.length &&
          round((outOfStockProductsSimilar.length / outOfStockProducts.length) * 100, 2),
        'out of stock rate':
          ingredientsArticles.length &&
          round((outOfStockProducts.length / ingredientsArticles.length) * 100, 2),
        ...analyticsProperties,
      });
    }
  }, [
    recipe,
    eventSource,
    categoryName,
    subcategoryName,
    analyticsProperties,
    ingredientsArticles,
  ]);

  return { analyticsProperties };
};

export default useRecipeAnalytics;
